import { useMemo } from 'react'
import { CircleMarker, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import type { GeoEngineer } from '../api/types'
import { useGeoStats } from '../hooks/useApi'
import { tokens } from '../theme'

// Leaflet's default marker icon URLs don't resolve under Vite's asset
// pipeline. Supplying the icon URLs explicitly avoids the 404 for
// marker-icon.png on first render.
const engineerIcon = (state: string) =>
  L.divIcon({
    className: 'fl-engineer-marker',
    html: `<div style="
      width:24px;height:24px;border-radius:50%;
      background:${engineerColour(state)};
      border:2px solid #fff;
      box-shadow:0 0 0 2px ${engineerColour(state)};
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-size:10px;font-weight:700;
    ">ENG</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })

function engineerColour(state: string): string {
  switch (state) {
    case 'active':
    case 'onsite':
    case 'enroute':
      return tokens.indigo
    case 'idle':
      return tokens.gold
    case 'break':
      return tokens.textSec
    case 'offroute':
    case 'overdue_checkin':
      return tokens.alert
    default:
      return '#9AA3AE'
  }
}

function severityColour(sev: string): string {
  if (sev === 'critical') return tokens.alert
  if (sev === 'high') return '#E09935'
  return tokens.gold
}

export default function MapView() {
  const { data, isLoading, isError, error } = useGeoStats()

  const engineersWithPos = useMemo(
    () => (data?.engineers ?? []).filter((e) => e.latitude != null && e.longitude != null),
    [data],
  )

  // premises_aggregate can be null when the backend returns no rows — the
  // useMemo below dereferences .length / .reduce so guard once here.
  const premisesAgg = useMemo(() => data?.premises_aggregate ?? [], [data])
  const exceptions = useMemo(() => data?.exceptions ?? [], [data])

  // Fit the view to the centroid of the premises cluster — robust even if the
  // seed data shifts postcodes around.
  const center = useMemo<[number, number]>(() => {
    if (premisesAgg.length === 0) return [51.288, -0.962]
    const sum = premisesAgg.reduce(
      (acc, p) => {
        acc.lat += p.latitude * p.open_count
        acc.lng += p.longitude * p.open_count
        acc.w += p.open_count
        return acc
      },
      { lat: 0, lng: 0, w: 0 },
    )
    if (sum.w === 0) return [51.288, -0.962]
    return [sum.lat / sum.w, sum.lng / sum.w]
  }, [premisesAgg])

  return (
    <Box
      sx={{
        // Fill the shell's content region. calc keeps the map from
        // pushing the whole page taller than the viewport.
        height: 'calc(100vh - 56px)',
        p: 2,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        minHeight: 0,
      }}
    >
        <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 520 }}>
          {isLoading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, bgcolor: 'rgba(255,255,255,0.6)' }}>
              <CircularProgress />
            </Box>
          )}
          {isError && (
            <Alert severity="error" variant="outlined" sx={{ m: 2 }}>
              Failed to load geo data: {error instanceof Error ? error.message : 'unknown'}
            </Alert>
          )}
          <MapContainer center={center} zoom={10} style={{ width: '100%', height: '100%', minHeight: 520 }} scrollWheelZoom>
            {/*
              CartoDB Voyager tiles — free, no API key, no per-referer policy.
              Falls back to OSM.fr if Carto is blocked in the deployment
              environment. OSM's volunteer servers are policy-blocked when
              the browser doesn't send a Referer header.
            */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              subdomains="abcd"
              maxZoom={19}
            />

            {/* Postcode heat bubbles — size by total, colour by critical/high share. */}
            {premisesAgg.map((p) => {
              const radius = Math.min(40, 8 + Math.sqrt(p.open_count) * 3)
              const hot = p.critical > 0 ? tokens.alert : p.high > 0 ? '#E09935' : tokens.gold
              return (
                <CircleMarker
                  key={`pa-${p.postcode}`}
                  center={[p.latitude, p.longitude]}
                  radius={radius}
                  pathOptions={{ color: hot, fillColor: hot, fillOpacity: 0.22, weight: 1 }}
                >
                  <Popup>
                    <strong>{p.postcode}</strong>
                    <br />
                    {p.open_count} open
                    {p.critical > 0 && <> · {p.critical} critical</>}
                    {p.high > 0 && <> · {p.high} high</>}
                  </Popup>
                </CircleMarker>
              )
            })}

            {/* Critical + high exception points. */}
            {exceptions.map((ex) => (
              <CircleMarker
                key={`ex-${ex.exception_id}`}
                center={[ex.latitude, ex.longitude]}
                radius={6}
                pathOptions={{
                  color: severityColour(ex.severity),
                  fillColor: severityColour(ex.severity),
                  fillOpacity: 0.9,
                  weight: 2,
                }}
              >
                <Popup>
                  <div style={{ fontWeight: 700 }}>{ex.code}</div>
                  <div>{ex.subject_summary}</div>
                  <div style={{ color: '#6B7280', fontSize: 11 }}>
                    {ex.severity} · opened {new Date(ex.opened_at).toLocaleTimeString()}
                  </div>
                </Popup>
              </CircleMarker>
            ))}

            {/* Engineers with last-known GPS. */}
            {engineersWithPos.map((e) => (
              <EngineerMarker key={e.engineer_id} e={e} />
            ))}
          </MapContainer>
        </Paper>

        <MapLegend engineersWithPos={engineersWithPos.length} totalEngineers={data?.engineers.length ?? 0} />
    </Box>
  )
}

function EngineerMarker({ e }: { e: GeoEngineer }) {
  if (e.latitude == null || e.longitude == null) return null
  const shiftLine = formatShiftForMarker(e)
  return (
    <Marker position={[e.latitude, e.longitude]} icon={engineerIcon(e.state)}>
      <Popup>
        <div style={{ fontWeight: 700 }}>{e.display_name}</div>
        <div style={{ color: '#6B7280', fontSize: 11 }}>{e.external_ref}</div>
        <div style={{ marginTop: 4 }}>
          State: <strong style={{ textTransform: 'capitalize' }}>{e.state}</strong>
        </div>
        <div style={{ marginTop: 2, color: '#6B7280', fontSize: 11 }}>{shiftLine}</div>
        {e.open_count > 0 && (
          <div style={{ marginTop: 4 }}>
            {e.open_count} open exception{e.open_count === 1 ? '' : 's'}
          </div>
        )}
      </Popup>
    </Marker>
  )
}

function formatShiftForMarker(e: GeoEngineer): string {
  if (!e.shift_planned_start || !e.shift_planned_end) return 'Off today'
  try {
    const start = new Date(e.shift_planned_start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    const end = new Date(e.shift_planned_end).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    const status = e.shift_status ?? 'planned'
    return `Shift ${start}–${end} · ${status}`
  } catch {
    return 'Off today'
  }
}

function MapLegend({ engineersWithPos, totalEngineers }: { engineersWithPos: number; totalEngineers: number }) {
  return (
    <Paper variant="outlined" sx={{ width: { xs: '100%', md: 240 }, p: 2, display: { xs: 'none', sm: 'block' } }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Legend
      </Typography>

      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        Engineer state
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <LegendDot colour={tokens.indigo} label="Active / Onsite / Enroute" />
        <LegendDot colour={tokens.gold} label="Idle" />
        <LegendDot colour={tokens.textSec} label="Break" />
        <LegendDot colour={tokens.alert} label="Off-route / overdue check-in" />
        <LegendDot colour="#9AA3AE" label="Offline" />
      </Stack>

      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        Exceptions
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <LegendDot colour={tokens.alert} label="Critical" />
        <LegendDot colour="#E09935" label="High" />
        <LegendDot colour={tokens.gold} label="Warn (aggregate only)" />
      </Stack>

      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        Postcode bubbles
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        Size proportional to open-exception count; colour takes the worst severity in the cluster.
      </Typography>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {engineersWithPos} of {totalEngineers} engineer(s) have a known position. Others are offline or haven't reported GPS.
      </Typography>
      <Chip
        size="small"
        label="Data refreshes every 15 s"
        sx={{ mt: 1.5, bgcolor: tokens.goldLight, color: tokens.charcoal }}
      />
    </Paper>
  )
}

function LegendDot({ colour, label }: { colour: string; label: string }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: colour, flexShrink: 0 }} />
      <Typography variant="body2">{label}</Typography>
    </Stack>
  )
}
